import { Injectable } from '@angular/core';
import { BookingsService, StorageService } from '../services';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { initialBookingsState } from '../store/initialisation/bookings';
import { objectToCamel } from 'ts-case-convert';
import { IBooking, IBase } from 'src/app/models';
import { ExpeditionStatus, ExpeditionType } from 'src/app/models/enums';
import { initialPaginationState } from '../store/initialisation/pagination';
import { APP } from '../config/app';
import {
  DEFAULT_BOOKING_STATUS,
  IBookingFilters,
  USER_DEFAULT_BOOKING_STATUS,
  deserializeBookingFilters,
  parseBookingFiltersFromStorage,
} from '../filters';

@Injectable({
  providedIn: 'root',
})
export class BookingsResolver {
  // Tracks the latest list-fetch request; stale completions are dropped so that
  // a fast hydrate-driven re-resolve isn't overwritten by the slower initial one.
  private listGeneration = 0;
  // Fingerprint of the inputs of the last successful list fetch. Used to skip
  // redundant fetches when navigating between /bookings and /bookings/:id (which
  // are sibling routes, so Angular re-runs the resolver even though the list
  // inputs haven't changed). The 60s interval in the component still handles
  // staleness and bypasses the resolver entirely.
  private lastFetchFingerprint: string | null = null;
  // Fingerprint of just the filters (without page). Lets us tell a pure page
  // change from a filter/search change so the pagination only collapses to the
  // loading "currentPage ···" form when the totals are genuinely unknown.
  private lastFilterFingerprint: string | null = null;

  constructor(
    private bookingsService: BookingsService,
    public storageService: StorageService,
  ) {}

  invalidateListCache() {
    this.lastFetchFingerprint = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    this.storageService.setRouteName('bookings');
    // /bookings/new hides the list behind the map; fetching here only trashes the cached rows.
    if (route.data['new']) return;
    // Read page and size from URL query params first, then fallback to data or default
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0)
      ? pageFromUrl
      : (route.data['bookings']?.pagination.actualPage || 1);
    const sizeParam = route.queryParamMap.get('size');
    const sizeFromUrl = sizeParam ? Number(sizeParam) : null;
    const size = (sizeFromUrl && !isNaN(sizeFromUrl) && sizeFromUrl > 0)
      ? sizeFromUrl
      : APP.DEFAULT_PAGE_SIZE;

    const userId = route.paramMap.get('userId');
    const defaultStatus = userId ? USER_DEFAULT_BOOKING_STATUS : DEFAULT_BOOKING_STATUS;
    const search = route.queryParamMap.get('search') ?? undefined;
    const filters = await this.resolveFilters(route, defaultStatus);

    const fingerprint = this.buildFingerprint(userId, size, page, filters, search);
    if (
      fingerprint === this.lastFetchFingerprint &&
      initialBookingsState.value?.length
    ) {
      // Same inputs as the last successful fetch — list is already in view. The
      // periodic interval (bookings.component) refreshes for staleness.
      return;
    }

    // Always blank the list when the fingerprint differs (page change, filter
    // change, search change) so the skeleton appears for any user-driven fetch.
    // The path-level case (/bookings -> /bookings/:id) is already short-circuited
    // by the fingerprint match above. The 60s polling refresh bypasses resolve()
    // entirely (calls bookingsResolver.getBookings directly), so it stays silent.
    initialBookingsState.isFetchingBookings = true;
    initialBookingsState.value = [];
    // Only clear pagination totals when filters/search changed. On a pure page
    // change we already know how many pages exist, so the pagination keeps the
    // full numbered list; on a filter change the totals are unknown so the
    // pagination collapses to "currentPage ···" until the response lands.
    const filterFingerprint = this.buildFingerprint(userId, size, 0, filters, search);
    if (filterFingerprint !== this.lastFilterFingerprint) {
      initialBookingsState.pagination.totalPages = 0;
      initialBookingsState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint = filterFingerprint;
    // Fire-and-forget mirrors prior behavior: resolver returns void, component renders skeletons,
    // initialBookingsState is mutated when the API call completes.
    if (userId) {
      this.resolveByUser(Number(userId), size, page, filters, search, fingerprint);
    } else {
      this.resolveAsync(size, page, filters, search, fingerprint);
    }
  }

  private buildFingerprint(
    userId: string | null,
    size: number,
    page: number,
    filters: IBookingFilters,
    search: string | undefined,
  ): string {
    return JSON.stringify({
      userId: userId ?? null,
      size,
      page,
      search: search ?? null,
      cancelled: filters.cancelled,
      faults: filters.faults,
      origin: filters.origin,
      destination: filters.destination,
      clients: filters.selectedClients.map((c) => c.id).sort(),
      towns: filters.selectedTowns.map((t) => t.id).sort(),
      services: filters.selectedServices.map((s) => s.id).sort(),
      routes: filters.selectedRoutes.map((r) => r.id).sort(),
      types: [...filters.selectedTypes].sort(),
      status: [...filters.selectedStatus].sort(),
    });
  }

  private async resolveFilters(
    route: ActivatedRouteSnapshot,
    defaultStatus: ExpeditionStatus[]
  ): Promise<IBookingFilters> {
    // URL is the source of truth whenever there is *any* query param (filter or not).
    // Only fall back to storage on a truly bare URL — that's the "I just clicked the
    // sidebar" entry path where we want to restore the user's last-applied filters.
    if (route.queryParamMap.keys.length > 0) {
      return deserializeBookingFilters(route.queryParamMap);
    }
    const stored = parseBookingFiltersFromStorage(await this.storageService.getBookingsFilters());
    if (stored) {
      return { ...stored, selectedStatus: stored.selectedStatus?.length ? stored.selectedStatus : defaultStatus };
    }
    return {
      selectedClients: [],
      selectedTowns: [],
      selectedServices: [],
      selectedRoutes: [],
      selectedTypes: [],
      selectedStatus: defaultStatus,
      cancelled: false,
      faults: false,
      origin: '',
      destination: '',
    };
  }

  async resolveAsync(size: number, page: number, filters: IBookingFilters, search?: string, fingerprint?: string) {
    const myGen = ++this.listGeneration;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.bookingsService.getBookingsFiltered(
      size, page,
      filters.selectedClients, filters.selectedTowns, filters.selectedTypes, filters.selectedStatus,
      filters.cancelled, filters.faults, filters.selectedServices, filters.selectedRoutes,
      filters.origin, filters.destination, search
    );

    // Drop the response if a newer resolver call has started in the meantime.
    if (myGen !== this.listGeneration) return;

    return this.storageService.getUserSelected().then(
      (userId) => {
        if (myGen !== this.listGeneration) return;
        userId
          ? this.getFilteredData(content, first, last, size, userId)
          : this.getData(content, number_of_elements, total_elements, total_pages, first, last, page, size);
        if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
      }
    );
  }

  async resolveByUser(userId: number, size: number, page: number, filters: IBookingFilters, search?: string, fingerprint?: string) {
    const myGen = ++this.listGeneration;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.bookingsService.getBookingsByUser(
      userId, size, page,
      filters.selectedClients, filters.selectedTowns, filters.selectedTypes, filters.selectedStatus,
      filters.cancelled, filters.faults, filters.selectedServices, filters.selectedRoutes,
      filters.origin, filters.destination, search
    );
    if (myGen !== this.listGeneration) return;
    const result = this.getData(content, number_of_elements, total_elements, total_pages, first, last, page, size);
    if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
    return result;
  }

  async getBookingsByUser(
    route: ActivatedRouteSnapshot,
    userId: number,
    expeditionClients: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    cancelled: boolean,
    faults: boolean,
    services: IBase[],
    routes: IBase[],
    origin: string,
    destination: string,
    searchValue?: string
  ) {
    const page = route.data['bookings']?.pagination.actualPage || 1;
    const savedPageSize = await this.storageService.getBookingsPageSize();
    const size = route.data['bookings']?.pagination.size || savedPageSize || APP.DEFAULT_PAGE_SIZE;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.bookingsService.getBookingsByUser(userId, size, page, expeditionClients, expeditionTowns, expeditionTypes, expeditionStatus, cancelled, faults, services, routes, origin, destination, searchValue);
    return this.getData(
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
      page,
      size
    );
  }

  async getBookings(
    route: ActivatedRouteSnapshot,
    expeditionClients: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    cancelled: boolean,
    faults: boolean,
    services: IBase[],
    routes: IBase[],
    origin: string,
    destination: string,
    searchValue?: string
  ) {
    const page = route.data['bookings']?.pagination.actualPage || 1;
    const savedPageSize = await this.storageService.getBookingsPageSize();
    const size = route.data['bookings']?.pagination.size || savedPageSize || APP.DEFAULT_PAGE_SIZE;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.bookingsService.getBookingsFiltered(size, page, expeditionClients, expeditionTowns, expeditionTypes, expeditionStatus, cancelled, faults, services, routes, origin, destination, searchValue);
    return this.getData(
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
      page,
      size
    );
  }


  getFilteredData(content: any, first: any, last: any, size: number, userId: any) {
    this.storageService.removeUserSelected();
    const data = initialBookingsState;
    const newValues = objectToCamel(content) as Array<IBooking>;
    data.value = newValues.filter((value) => value.userId === Number(userId));
    data.isFetchingBookings = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = data.value.length;
    data.pagination.totalPages = Math.ceil(data.value.length / size);
    data.pagination.totalElements = data.value.length;
    data.pagination.actualPage = 1;
    data.pagination.first = first;
    data.pagination.last = last;
    data.pagination.size = size;
    return data;
  }

  getData(
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
    size: any
  ) {
    const data = initialBookingsState;
    data.value = objectToCamel(content) as Array<IBooking>;
    data.value = data.value.map((booking) => ({
      ...booking,
      selected: false,
      selectable: true
    }));
    data.isFetchingBookings = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    data.pagination.size = size;
    return data;
  };
}
